import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { logout } from 'store/slices/authSlice';
import { PUBLIC_PATHS, ROUTES } from 'utils/constant';
import { Loader } from 'components/Loader/Loader';

/**
 * Authentication Middleware for handling auth state and protected routes
 * Provides middleware for API interceptors and React route protection
 *
 * Features:
 * - Automatic logout on unauthorized responses
 * - Smart redirect handling (avoids redirecting from public pages)
 * - Protected route component for React Router
 * - Integration with Redux auth state
 */

/**
 * Create authentication middleware for API interceptors
 * @param {Object} store - Redux store instance
 * @param {Function} navigate - Optional React Router navigate function
 * @returns {Object} Middleware object with onUnauthorized and onError handlers
 */
export const createAuthMiddleware = (store, navigate = null) => {
  return {
    /**
     * Handle unauthorized (401) responses
     * Clears auth state and redirects to login if on protected route
     */
    onUnauthorized: () => {
      // Dispatch logout action to clear auth state
      store.dispatch(logout());

      // Only redirect if we're on a protected route (not login/register/public pages)
      const currentPath = window.location.pathname;
      const isPublicPath = PUBLIC_PATHS.some(
        (path) => currentPath === path || currentPath.startsWith(path + '/')
      );

      if (!isPublicPath && navigate) {
        navigate(ROUTES.HOME);
      } else if (!isPublicPath && !navigate) {
        // Fallback to window.location if navigate is not available
        window.location.href = ROUTES.HOME;
      }
    },

    /**
     * Handle general authentication errors
     * @param {Error} error - Error object from failed request
     */
    onError: (error) => {
      // Handle other auth-related errors if needed
      console.error('Auth error:', error);
    },
  };
};

/**
 * Protected Route component for React Router
 * Redirects to login page if user is not authenticated
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {React.ReactElement} Children if authenticated, Navigate to login otherwise
 */
const ProtectedRouteBase = ({ children }) => {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);

  // Show minimal loading indicator while auth is being checked
  if (loading) {
    return <Loader size='large' variant='spinner' showText={false} />;
  }

  // Only redirect to login if auth check is complete and user is not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return children;
};

ProtectedRouteBase.propTypes = {
  children: PropTypes.node.isRequired,
};

ProtectedRouteBase.displayName = 'ProtectedRoute';

export const ProtectedRoute = React.memo(ProtectedRouteBase);
